import { cartService } from '@/services/cart.service';
import { Product } from '@/types/cart';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { produce } from 'immer';
import { useCartStore } from './cart-store';

interface ProductState {
  products: Product[];
  updateProducts: (products: Product[], init?: boolean) => void;
  addProduct: (products: Product, isFBP?: number) => void;
  changeQuantity: (catalogNumber: string, quantity: number, fbpQuantity?: number) => void;
  removeProduct: (catalogNumber: string | string[]) => void;
  initProducts: (products: Product[]) => void;
  isUpdating: boolean;
  setIsUpdating: (isUpdating: boolean) => void;
}

export const useProductStore = create<ProductState>()(
  devtools(
    immer((set, get) => ({
      products: [],
      initProducts: (products: Product[]) => {
        set((state) => {
          state.products = products;
        });
      },
      updateProducts: async (products: Product[] = []) => {
        try {
          get().setIsUpdating(true);
          const { data, code } = await cartService.updateProducts(
            products.map((p) => ({
              itemId: p.id,
              quantity: p.quantity,
              catalogNumber: p.catalogNumber,
            }))
          );
          if (code === '200') {
            get().setIsUpdating(false);
            products = products.map((p) => {
              const newProduct = data.find((d) => d.catalogNumber === p.catalogNumber);
              if (newProduct) {
                return {
                  ...p,
                  id: newProduct.id,
                };
              } else {
                return p;
              }
            });
          } else {
            get().setIsUpdating(false);
          }
        } catch (error) {
          get().setIsUpdating(false);
        }
        set((state) => {
          state.products = products;
        });
        if (window.miniCart?.setCurrentQuantity) {
          window.miniCart?.setCurrentQuantity();
        }
      },
      changeQuantity: (catalogNumber, quantity, fbpQuantity?) => {
        const newProducts = produce(get().products, (draft) => {
          let index = draft.findIndex((product) => product.catalogNumber === catalogNumber);
          if (fbpQuantity !== undefined) {
            index = draft.findIndex(
              (product) =>
                product.catalogNumber === catalogNumber &&
                (product.quantity !== fbpQuantity || product?.canApplyPromotion === false)
            );
          } else {
            index = draft.findIndex((product) => product.catalogNumber === catalogNumber);
          }
          draft[index].quantity = quantity;
        });
        get().updateProducts(newProducts);
      },
      removeProduct: async (catalogNumber) => {
        if (!Array.isArray(catalogNumber)) {
          catalogNumber = [catalogNumber];
        }
        const newSelectedCatalogNumbers = [...useCartStore.getState().selectedCatalogNumbers];
        const newSelectedProducts = produce(get().products, (draft) => {
          return draft.filter((product) => !(catalogNumber as string[]).includes(product.catalogNumber));
        });
        const newSelectedIds = newSelectedCatalogNumbers.filter((no) => !(catalogNumber as string[]).includes(no));
        useCartStore.getState().setSelectedCatalogNumbers(newSelectedIds);
        await get().updateProducts(newSelectedProducts);
      },
      addProduct: (product, fbpQuantity?) => {
        const products = get().products;
        let duplicated = false;
        const newProduct = products.map((p) => {
          let qty = p.quantity;
          if (p.catalogNumber === product.catalogNumber) {
            if (fbpQuantity !== undefined) {
              if (
                product.catalogNumber === p.catalogNumber &&
                (p.quantity !== fbpQuantity || p?.canApplyPromotion === false)
              ) {
                qty += product.quantity;
              }
              duplicated = true;
              return {
                ...product,
                quantity: qty,
                ...(p.canApplyPromotion !== undefined ? { canApplyPromotion: p.canApplyPromotion } : {}),
              };
            } else if (p.productStatus !== 'RELEASED') {
              qty = 1;
            } else {
              qty += product.quantity;
            }
            duplicated = true;
            return {
              ...product,
              quantity: qty,
            };
          } else {
            return {
              ...p,
              quantity: qty,
            };
          }
        });
        if (duplicated) {
          get().updateProducts(newProduct);
        } else {
          const newProducts = [...get().products, product];
          get().updateProducts(newProducts);
        }
      },
      isUpdating: false,
      setIsUpdating: (isUpdating) => {
        set((state) => {
          state.isUpdating = isUpdating;
        });
      },
    })),
    {
      name: 'cart-products',
    }
  )
);
